import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { TransformedCustomViewContainer } from "../../components/transformed_custom_view";
import { defaultConfig, defaultConfigGoodchop } from "./default_config";
import { PivotControl } from "../../constants/pivot_control_options";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { SupplyWalkEditableCustomControls } from "./custom_controls";
import { getSupplyWalkEditableViewMatrix, buildPoRowKey } from "./get_matrix";
import type { ApprovedPoSummary } from "./get_matrix";
import {
  AddSkuDialog,
  type AddSkuLine,
  type AddSkuTarget,
} from "./add_sku_dialog";
import { useSkuCatalog } from "./use_sku_catalog";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { ChatContext } from "../../contexts/chat/context";
import { LayoutContext } from "../../contexts/layout/context";
import {
  useApi,
  useCustomer,
  usePerspective,
} from "../../contexts/app/provider";
import { SysMessagesContext } from "../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../contexts/sys_messages/type";
import {
  SaveApprovedPOsBatchDocument,
  PoActionType,
} from "../../services/api/__generated__/backend_gateway-types";
import { InvalidateMyTransformationCachesDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { SupplyWalkEditsContext } from "../../contexts/supply_walk_edits/provider";
import { SupplyWalkEditableNestedDetails } from "./nested_details";
import { mergeSuggestedChange } from "./po_line_merge";
import {
  APPROVED_STATUS,
  OPEN_PO_MODS_STATUS,
  OPEN_STATUS,
  TBD_PO_ID,
  goodchopExportPlanForStatus,
  runGoodchopExportDownload,
  runGoodchopPdfZipDownload,
} from "./goodchop_export";
import { commonRunEditState } from "../../entities/common_run_edit_state";
import {
  createCommonViewContext,
  type CommonViewContextV1,
} from "../../entities/common_view_context";
import {
  renderedViewBindingIdentity,
  type RenderedViewBinding,
} from "../../entities/pivot_view_config";
import { isCommonPoInboxCurrentViewReceiptId } from "../../contexts/chat/nucleus_sse_client";
import {
  createPoInboxRowContext,
  isExplainablePoInboxStatus,
  poInboxRowPrompt,
  type CommonPoInboxRowContextV1,
} from "../../entities/po_inbox_row_context";
import { isCommonNucleusExplainabilityCustomer } from "../../entities/common_nucleus_customer_capabilities";
import {
  awaitNucleusExplainStart,
  NUCLEUS_EXPLAIN_BUSY_REASON,
  NUCLEUS_EXPLAIN_DIRTY_PO_REASON,
  NUCLEUS_EXPLAIN_PENDING_PO_REASON,
  NUCLEUS_EXPLAIN_STALE_REASON,
  rejectNucleusExplainActivation,
} from "../../entities/nucleus_explain_action";
import { useSynchronizedPending } from "../../components/nucleus_explain/use_synchronized_pending";

const PO_INBOX_STATUSES = new Set([
  "Delivered",
  "Open",
  "Needs Review",
  "Needs Review: New POs to Place",
  "Approved",
]);

const getRunIdentity = (
  run: React.ContextType<typeof VersionedDataContext>["mainRun"] | undefined,
): string | undefined =>
  run
    ? [
        run.runSettings.organizationId,
        run.runSettings.inputId,
        run.runSettings.workflowId,
        run.runSettings.settingsNameAndVersion,
        run.runSettings.runAttemptId,
      ].join("\u0000")
    : undefined;

const selectedPoInboxStatus = (
  params: DatePivotParams | undefined,
): string | undefined => {
  const statusFilters = params?.filters?.filter(
    (filter) =>
      Array.isArray(filter) &&
      filter.length === 3 &&
      filter[0] === "status" &&
      filter[1] === "in",
  );
  if (statusFilters?.length !== 1) return undefined;
  const values = statusFilters[0][2];
  if (!Array.isArray(values) || values.length !== 1) return undefined;
  const status = values[0];
  return typeof status === "string" && PO_INBOX_STATUSES.has(status)
    ? status
    : undefined;
};

type AcceptedPoInboxView = Readonly<{
  source: RenderedViewBinding;
  sourceIdentity: string;
  runIdentity: string;
  status: string;
  viewContext: CommonViewContextV1;
}>;

type PendingPoInboxRow = Readonly<{
  context: CommonPoInboxRowContextV1;
  prompt: string;
  sourceIdentity: string;
  runIdentity: string;
  viewContext: CommonViewContextV1;
  connectionReady: boolean;
}>;

// Sentinel source status for a hand-added line. Deliberately not one of the
// real bucket statuses, so the optimistic count bumps Approved without
// decrementing a bucket the line never belonged to.
const ADDED_LINE_SOURCE_STATUS = "Added line";

// TODO: Implement cascadeRecalculateNested helper when we have a way to modify Arrow table data

export const SupplyWalkEditableContainer = () => {
  const { apiService } = useApi();
  const { perspectiveApiService } = usePerspective();
  const { customerSettings, selectedCustomerId } = useCustomer();
  const {
    mainRun,
    lastSuccessfulRun,
    comparedRun = null,
  } = React.useContext(VersionedDataContext);
  const { isComparing, setIsSidebarOpen } = React.useContext(LayoutContext);
  const {
    connectChat,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    registerActiveMetricContext,
    registerCurrentViewData,
    sendMessage,
    transportKind,
  } = React.useContext(ChatContext);
  const { addSystemMessage } = React.useContext(SysMessagesContext);
  const supplyGuard = React.useContext(SupplyWalkEditsContext);
  const activeRun = mainRun || lastSuccessfulRun;
  const activeRunIdentity = getRunIdentity(activeRun);
  const isGoodchop = customerSettings?.id === "goodchop";

  // Persistent storage is now managed by the SupplyWalkEditsContext

  // Row edit state storage (temporary edits while editing)
  // This replaces the module-level globalRowEditState to properly scope state to this route
  const rowEditStateRef = React.useRef(
    new Map<
      string,
      {
        isEditing: boolean;
        editValues: Record<string, string>;
        committedValues: Record<string, string>;
        originalValues: Record<string, string>;
        listeners: Set<() => void>;
      }
    >(),
  ).current;

  // Pending-save queue. Clicks accumulate here; a 20-second interval drains
  // the entire queue with one saveApprovedPOsBatch mutation. This replaces
  // the old per-click round-trip pattern: N clicks now produce one S3 write
  // (read-modify-write of approved_pos.parquet) and one view-transform
  // re-fetch instead of N of each.
  const pendingSavesRef = React.useRef<
    Map<string, { rowData: any; checked: boolean }>
  >(new Map());
  const flushIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const isFlushingRef = React.useRef(false);
  // Bumped by every discard. A flush that started before the bump must not put
  // its snapshot back on failure — those clicks were discarded while the
  // request was in the air, and restoring them resurrects exactly what the
  // planner was told had been thrown away.
  const discardGenerationRef = React.useRef(0);
  // Set when a flush is asked for while one is already in flight, so the
  // in-flight one re-runs instead of the request being dropped. Adding a SKU
  // and editing a PO number both flush immediately on purpose; without this
  // they silently do nothing whenever a flush happens to be running, and the
  // planner watches the dialog close with no new row for up to 20 seconds.
  const flushAgainRef = React.useRef(false);
  const flushPendingSavesRef = React.useRef<(() => Promise<void>) | null>(null);
  const FLUSH_INTERVAL_MS = 20_000;
  const [acceptedPoInboxView, setAcceptedPoInboxView] =
    React.useState<AcceptedPoInboxView | null>(null);
  const {
    pending: pendingPoInboxRow,
    pendingRef: pendingPoInboxRowRef,
    setPending: setPendingPoInboxRow,
    clearPending: clearPendingPoInboxRow,
    replacePending: replacePendingPoInboxRow,
  } = useSynchronizedPending<PendingPoInboxRow>();
  const acceptedBindingKeyRef = React.useRef<string | undefined>(undefined);
  const releaseCurrentViewDataRef = React.useRef<(() => void) | null>(null);
  const releaseActiveMetricContextRef = React.useRef<(() => void) | null>(null);
  const activeRowIdentityRef = React.useRef<string | undefined>(undefined);
  const activeRunIdentityRef = React.useRef(activeRunIdentity);
  activeRunIdentityRef.current = activeRunIdentity;
  const acceptedPoInboxViewRef = React.useRef<AcceptedPoInboxView | null>(
    acceptedPoInboxView,
  );
  acceptedPoInboxViewRef.current = acceptedPoInboxView;

  const supportsPoInboxNucleus = Boolean(
    transportKind === "platform" &&
      activeRun &&
      activeRunIdentity &&
      activeRun.status === "RUN_COMPLETED" &&
      activeRun.runSettings.workflowId === "workflow_atomic_pivots" &&
      commonRunEditState(activeRun.runSettings.settingsNameAndVersion) &&
      !isComparing &&
      comparedRun === null &&
      isCommonNucleusExplainabilityCustomer(
        activeRun.runSettings.organizationId,
      ) &&
      (!selectedCustomerId ||
        selectedCustomerId === activeRun.runSettings.organizationId) &&
      (!customerSettings?.id ||
        customerSettings.id === activeRun.runSettings.organizationId),
  );
  const supportsPoInboxNucleusRef = React.useRef(supportsPoInboxNucleus);
  supportsPoInboxNucleusRef.current = supportsPoInboxNucleus;

  const clearActivePoInboxRow = React.useCallback(() => {
    releaseActiveMetricContextRef.current?.();
    releaseActiveMetricContextRef.current = null;
    activeRowIdentityRef.current = undefined;
    clearPendingPoInboxRow();
  }, [clearPendingPoInboxRow]);

  const handleRenderedSourceChange = React.useCallback(
    (
      source: RenderedViewBinding | undefined,
      viewParams?: DatePivotParams,
    ): void => {
      const status = selectedPoInboxStatus(viewParams);
      const viewContext = viewParams
        ? createCommonViewContext({
            surfaceId: "po-inbox",
            measure: null,
            dateHierarchy: viewParams.dateHierarchy ?? null,
            minDate: null,
            maxDate: null,
            segments: [],
            filters: viewParams.filters,
            postFilters: viewParams.postFilters,
          })
        : undefined;
      const sourceIdentity = source
        ? renderedViewBindingIdentity(source)
        : undefined;
      const sourceRunIdentity = getRunIdentity(source?.run);
      const isAccepted = Boolean(
        supportsPoInboxNucleus &&
          source &&
          sourceIdentity &&
          sourceRunIdentity &&
          sourceRunIdentity === activeRunIdentity &&
          isCommonPoInboxCurrentViewReceiptId(source.receiptId) &&
          status &&
          viewContext &&
          registerCurrentViewData,
      );
      const nextBindingKey = isAccepted
        ? JSON.stringify([
            sourceIdentity,
            sourceRunIdentity,
            status,
            viewContext,
          ])
        : undefined;
      if (acceptedBindingKeyRef.current === nextBindingKey) return;

      acceptedBindingKeyRef.current = nextBindingKey;
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
      clearActivePoInboxRow();
      acceptedPoInboxViewRef.current = null;
      setAcceptedPoInboxView(null);
      if (
        !isAccepted ||
        !source ||
        !sourceIdentity ||
        !sourceRunIdentity ||
        !status ||
        !viewContext ||
        !registerCurrentViewData
      ) {
        return;
      }

      releaseCurrentViewDataRef.current = registerCurrentViewData(
        {
          version: "CommonCurrentViewData.v1",
          surfaceId: "po-inbox",
          receiptId: source.receiptId!,
        },
        viewContext,
      );
      const nextAcceptedView = {
        source,
        sourceIdentity,
        runIdentity: sourceRunIdentity,
        status,
        viewContext,
      } as const;
      acceptedPoInboxViewRef.current = nextAcceptedView;
      setAcceptedPoInboxView(nextAcceptedView);
    },
    [
      activeRunIdentity,
      clearActivePoInboxRow,
      registerCurrentViewData,
      supportsPoInboxNucleus,
    ],
  );

  React.useEffect(
    () => () => {
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
      releaseActiveMetricContextRef.current?.();
      releaseActiveMetricContextRef.current = null;
    },
    [],
  );

  // Clear all edits when run changes to prevent cross-run contamination
  React.useEffect(() => {
    // Clear all edits when run changes
    rowEditStateRef.clear();
  }, [
    mainRun?.runSettings?.runAttemptId,
    lastSuccessfulRun?.runSettings?.runAttemptId,
    rowEditStateRef,
  ]);

  // Build the GraphQL PoActionInput for one queued click. Pure: reads
  // rowData + the supplyGuard edit context, returns null if there's no run
  // to attach to. The actual mutation is sent in batch by flushPendingSaves.
  const buildPoActionForRow = React.useCallback(
    (rowData: any, isApproving: boolean) => {
      const runAttemptId =
        mainRun?.runSettings?.runAttemptId ||
        lastSuccessfulRun?.runSettings?.runAttemptId ||
        "no-run";
      const rowKey = buildPoRowKey(rowData, runAttemptId, isGoodchop);
      const editData = supplyGuard?.getEdit?.(rowKey);
      const editedValues = editData?.committedValues || {};

      const quantity = editedValues.quantity
        ? parseFloat(editedValues.quantity)
        : rowData.quantity;
      const unitCost = editedValues.unit_cost
        ? parseFloat(editedValues.unit_cost)
        : rowData.unit_cost;

      // What the addition itself is worth, which a planner may have priced by
      // hand rather than by the quantity.
      const addedTotal = editedValues.total_cost
        ? parseFloat(editedValues.total_cost)
        : rowData.total_cost;
      // Only a suggested change joins a line; every other bucket is already the
      // line it will be saved as.
      const isSuggestedChange = rowData.status === OPEN_PO_MODS_STATUS;
      const merged = mergeSuggestedChange(
        { quantity, unitCost, totalCost: addedTotal },
        isSuggestedChange
          ? {
              quantity: rowData.baseline_sku_quantity,
              unitCost: rowData.baseline_sku_unit_cost,
              placedDate: rowData.baseline_sku_order_placed_date,
              deliveryDate: rowData.baseline_sku_order_delivery_date,
            }
          : {},
      );

      return {
        purchase_order_id: rowData.purchase_order_id,
        sku: rowData.sku,
        sku_name: rowData.sku_name,
        location: rowData.location,
        supplier: rowData.supplier,
        quantity: merged.quantity,
        total_cost: merged.totalCost,
        unit_cost: merged.unitCost,
        order_placed_date:
          merged.placedDate ||
          editedValues.order_placed_date ||
          rowData.order_placed_date,
        order_delivery_date:
          merged.deliveryDate ||
          editedValues.order_delivery_date ||
          rowData.order_delivery_date,
        includes_modification: isSuggestedChange,
        pallets: rowData.pallets != null ? Number(rowData.pallets) : null,
        // Assigned by the save on first approval; a planner edit on the Approved
        // tab comes back through here and replaces it.
        po_number: editedValues.po_number ?? rowData.po_number ?? "",
        // Set only by the add-a-SKU flow; the save rejects such a line if the
        // PO already carries that SKU instead of overwriting the existing one.
        is_new_line: rowData.__isNewLine === true,
        action: isApproving ? PoActionType.Approve : PoActionType.Unapprove,
      };
    },
    [mainRun, lastSuccessfulRun, supplyGuard, isGoodchop],
  );

  // Drain the pending-saves queue with ONE batch mutation. Snapshot + clear
  // up front so clicks arriving during the network round-trip land in the
  // next interval rather than the in-flight batch.
  // "Discard changes" clears the edit store, but the queued approve/un-approve
  // clicks live here — without this the unmount flush persists the very changes
  // the planner was told were discarded.
  React.useEffect(() => {
    const onDiscard = () => {
      discardGenerationRef.current += 1;
      pendingSavesRef.current.clear();
      supplyGuard?.setPendingSaveCount?.(0);
    };
    window.addEventListener("supplyEditsDiscarded", onDiscard);
    return () => window.removeEventListener("supplyEditsDiscarded", onDiscard);
  }, [supplyGuard]);

  const flushPendingSaves = React.useCallback(async () => {
    if (isFlushingRef.current) {
      flushAgainRef.current = true;
      return;
    }
    if (pendingSavesRef.current.size === 0) return;
    const runToUse = mainRun || lastSuccessfulRun;
    if (!runToUse) return;

    isFlushingRef.current = true;
    const snapshot = Array.from(pendingSavesRef.current.entries());
    const generation = discardGenerationRef.current;
    pendingSavesRef.current.clear();
    // The guard stays armed until the mutation actually lands — until then the
    // snapshot is the only copy of these clicks, and navigating away loses them.

    try {
      const actions = snapshot
        .map(([, payload]) =>
          buildPoActionForRow(payload.rowData, payload.checked),
        )
        .filter((a): a is NonNullable<typeof a> => a != null);

      if (actions.length === 0) {
        // The queue was already drained above, so nothing is pending — leaving
        // without resetting would arm the guard for the rest of the session.
        supplyGuard?.setPendingSaveCount?.(pendingSavesRef.current.size);
        return;
      }

      const saveResult = await apiService.getClient().mutate({
        mutation: SaveApprovedPOsBatchDocument,
        variables: {
          runSettings: {
            inputId: runToUse.runSettings.inputId,
            workflowId: runToUse.runSettings.workflowId,
            settingsNameAndVersion: runToUse.runSettings.settingsNameAndVersion,
            runAttemptId: runToUse.runSettings.runAttemptId,
          },
          actions,
        },
      });

      // Landed: only what was queued mid-flight is still unsaved.
      supplyGuard?.setPendingSaveCount?.(pendingSavesRef.current.size);

      // A dropped line is invisible otherwise: the row simply never appears.
      const rejectedCount =
        saveResult?.data?.saveApprovedPOsBatch?.rejectedDuplicateCount ?? 0;
      const clashCount =
        saveResult?.data?.saveApprovedPOsBatch?.rejectedNumberClashCount ?? 0;
      if (clashCount > 0) {
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message:
            "That PO number already belongs to a different supplier or location, so the change was not applied. Two vendors cannot share a PO number.",
        });
      }
      if (rejectedCount > 0) {
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message:
            rejectedCount === 1
              ? "That SKU is already on this PO, so the line was not added. Edit the existing line instead."
              : `${rejectedCount} SKUs are already on their PO, so those lines were not added. Edit the existing lines instead.`,
        });
      }

      // Drop the perspective server's cached transform results for this run so
      // the next subscribe re-runs against the just-written approved_pos. The
      // browser calls the perspective server directly (its public ALB) — the
      // in-VPC backend gateway can't reach it, which is why the old server-side
      // invalidation silently no-op'd ("fetch failed" / APP-299). Best-effort:
      // the save already persisted; a stale cache otherwise self-heals on TTL.
      try {
        await perspectiveApiService.getClient().mutate({
          mutation: InvalidateMyTransformationCachesDocument,
          variables: {
            runSettings: {
              inputId: runToUse.runSettings.inputId,
              workflowId: runToUse.runSettings.workflowId,
              settingsNameAndVersion:
                runToUse.runSettings.settingsNameAndVersion,
              runAttemptId: runToUse.runSettings.runAttemptId,
            },
          },
        });
      } catch (invalidateErr) {
        console.warn(
          "failed to invalidate perspective transformation caches",
          invalidateErr,
        );
      }
      supplyGuard?.bumpReloadVersion?.();
    } catch (error) {
      // Restore the snapshot to the head of the queue so clicks aren't lost
      // — the next interval (or unmount flush) will retry them. Unless the
      // planner discarded while this was in flight: then there is nothing to
      // retry, and re-queueing would bring the discarded clicks back and save
      // them on the next interval.
      if (generation === discardGenerationRef.current) {
        for (const [rowKey, payload] of snapshot) {
          if (!pendingSavesRef.current.has(rowKey)) {
            pendingSavesRef.current.set(rowKey, payload);
          }
        }
      }
      // Re-arm the guard: these clicks are unsaved again until the retry lands.
      supplyGuard?.setPendingSaveCount?.(pendingSavesRef.current.size);
      const errorMsg =
        (error as Error)?.message || String(error) || "Unknown error";
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message: `Failed to save approved POs: ${errorMsg}`,
      });
    } finally {
      isFlushingRef.current = false;
      if (flushAgainRef.current) {
        flushAgainRef.current = false;
        // Via the ref, not a direct self-call: this callback is a const and
        // cannot reference itself. Deferred a tick so the finally unwinds first.
        setTimeout(() => void flushPendingSavesRef.current?.(), 0);
      }
    }
  }, [
    mainRun,
    lastSuccessfulRun,
    apiService,
    perspectiveApiService,
    supplyGuard,
    buildPoActionForRow,
    addSystemMessage,
  ]);

  React.useEffect(() => {
    flushPendingSavesRef.current = flushPendingSaves;
  }, [flushPendingSaves]);

  // 20-second batch flush interval. Replaces the old per-click 1-second
  // debounce + per-row mutation: clicks accumulate locally and one batch
  // mutation lands every 20s, producing one S3 write + one view-transform
  // re-fetch per interval regardless of click volume.
  React.useEffect(() => {
    flushIntervalRef.current = setInterval(() => {
      void flushPendingSaves();
    }, FLUSH_INTERVAL_MS);
    return () => {
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
        flushIntervalRef.current = null;
      }
      // Best-effort flush of anything pending when the component unmounts so
      // the user doesn't lose clicks on navigation.
      void flushPendingSaves();
    };
  }, [flushPendingSaves]);

  // Belt-and-suspenders: also flush when the tab becomes hidden so closing
  // the tab / switching apps doesn't drop pending clicks beyond the 20s
  // window. The unmount flush above handles in-app navigation.
  React.useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden") {
        void flushPendingSaves();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [flushPendingSaves]);

  // Handle checkbox toggle: apply optimistic state, then enqueue a save.
  const applyApprovedToggle = React.useCallback(
    (rowKey: string, rowData: any, checked: boolean) => {
      // Read the live approval state BEFORE updating it. rowData.approved is
      // the server's view, which still says true immediately after an
      // un-approve — counting from it would miss the re-approve that follows.
      const storedApproval = supplyGuard?.getEdit?.(rowKey)?.approved;
      const wasApproved =
        storedApproval === undefined
          ? rowData?.approved === true
          : storedApproval === true;

      // Update the edit context immediately for navigation guard
      if (supplyGuard?.setRowApproved) {
        supplyGuard.setRowApproved(rowKey, checked);
      }

      // Count only genuine transitions. Re-approving an already-approved row is
      // a PO-number edit, not a new approval: counting it either way drifts the
      // total, and at zero hasAnyApprovedRows gates off the PDF/CSV download.
      if (checked && !wasApproved) {
        supplyGuard?.incrementApprovedRowCount?.();
        // Record the source bucket so the status-bar tab counts decrement
        // immediately on click. The DUMMY-row counts baked into the parquet
        // only update after save → server cache invalidate → re-subscribe →
        // fresh transform — a multi-second round trip the user shouldn't see.
        // Skip when source is already "Approved" (unapprove-then-approve on
        // the Approved tab) since there's no source bucket to decrement.
        const sourceStatus = String(rowData?.status ?? "");
        if (sourceStatus && sourceStatus !== "Approved") {
          supplyGuard?.recordPendingApproval?.(rowKey, sourceStatus);
        }
      } else if (!checked && wasApproved) {
        supplyGuard?.decrementApprovedRowCount?.();
        // Drop the optimistic delta if we created one this session. We can't
        // restore the source bucket count for a row that was already approved
        // before page load (the original status isn't tracked) — those refresh
        // on the next data update.
        supplyGuard?.clearPendingApproval?.(rowKey);
      }

      // Last click wins for a given row in the current 20-second window.
      // The recurring flush interval picks the entry up on its next tick.
      pendingSavesRef.current.set(rowKey, { rowData, checked });
      // Keep the navigation guard honest: these clicks are unsaved until the
      // batch flush drains them.
      supplyGuard?.setPendingSaveCount?.(pendingSavesRef.current.size);
    },
    [supplyGuard],
  );

  // Taking one line back withdraws the whole order, and the planner's own work
  // on it goes with it: edited quantities revert to the plan's and a hand-added
  // line has nowhere else to live. Asked before, because nothing on screen says
  // so afterwards — the order simply reappears shorter.
  const [pendingUnapprove, setPendingUnapprove] = React.useState<{
    rowKey: string;
    rowData: any;
    answer: (confirmed: boolean) => void;
  } | null>(null);
  // Read by the click handler, which must not re-subscribe every checkbox in the
  // grid each time this opens and closes.
  const pendingUnapproveRef = React.useRef(false);

  const handleApprovedToggle = React.useCallback(
    (rowKey: string, rowData: any, checked: boolean) => {
      const isPlacedOrder =
        isGoodchop &&
        String(rowData?.purchase_order_id ?? "") !== TBD_PO_ID &&
        !!rowData?.purchase_order_id;
      if (!checked && isPlacedOrder) {
        // One question at a time. A second row asked while the first is still on
        // screen is refused outright, so its tick comes straight back rather
        // than waiting on an answer that would never arrive.
        if (pendingUnapproveRef.current) return false;
        pendingUnapproveRef.current = true;
        // The caller puts the tick back if they decline.
        return new Promise<boolean>((resolve) =>
          setPendingUnapprove({ rowKey, rowData, answer: resolve }),
        );
      }
      applyApprovedToggle(rowKey, rowData, checked);
      return true;
    },
    [applyApprovedToggle, isGoodchop],
  );

  const resolveUnapprove = React.useCallback(
    (confirmed: boolean) => {
      const pending = pendingUnapprove;
      if (!pending) return;
      setPendingUnapprove(null);
      pendingUnapproveRef.current = false;
      if (confirmed) {
        applyApprovedToggle(pending.rowKey, pending.rowData, false);
      }
      pending.answer(confirmed);
    },
    [applyApprovedToggle, pendingUnapprove],
  );

  // --- Add a SKU the engine never suggested -------------------------------
  // The planner picks the PO to add to and the line inherits its vendor,
  // ship-to and both dates, or opens a new PO on a chosen lane and week. Either
  // way it is approved on creation: approved_pos is the only writable store, so
  // an added line has nowhere else to live.
  const [addSkuOpen, setAddSkuOpen] = React.useState(false);
  // Written by the matrix during render; read only when the dialog opens, so a
  // ref avoids a re-render on every data change.
  const poListRef = React.useRef<ApprovedPoSummary[] | null>(null);
  const [approvedPos, setApprovedPos] = React.useState<ApprovedPoSummary[]>([]);
  // Latched so the catalog transform is fetched once, on first use, and not for
  // planners who never open the dialog.
  const [skuCatalogRequested, setSkuCatalogRequested] = React.useState(false);
  const skuCatalog = useSkuCatalog(
    (mainRun || lastSuccessfulRun)?.runSettings || null,
    skuCatalogRequested,
  );

  const handleAddSkuOpen = React.useCallback(() => {
    setSkuCatalogRequested(true);
    setApprovedPos(poListRef.current ?? []);
    setAddSkuOpen(true);
  }, []);

  const handleAddSkuConfirm = React.useCallback(
    (line: AddSkuLine, target: AddSkuTarget) => {
      setAddSkuOpen(false);
      const runAttemptId =
        mainRun?.runSettings?.runAttemptId ||
        lastSuccessfulRun?.runSettings?.runAttemptId ||
        "no-run";

      // A line added to an order already with a vendor belongs to that order and
      // carries its number; one opening a new order carries the "TBD" sentinel,
      // which the approved-POs export and the PDF numbering both key on.
      //
      // status is a sentinel, not a real bucket: it has to be non-empty and not
      // "Approved" for the toggle to record an optimistic delta (which is what
      // moves the Approved tab count straight away), while not matching any
      // bucket key, so nothing gets decremented — an added line was never in
      // Needs Review to move out of.
      const rowData = {
        purchase_order_id: target.purchaseOrderId || TBD_PO_ID,
        sku: line.sku,
        sku_name: line.skuName,
        // From the TARGET, not the catalog entry. rowKeyOf compares supplier and
        // location verbatim (only poGroupKey normalises), so taking the PO's own
        // spelling is what lets the duplicate-line guard see this line against
        // the lines it is joining. For a new PO the target carries the catalog
        // spelling anyway, so this is the PO's lane in both cases.
        location: target.location,
        supplier: target.supplier,
        quantity: line.quantity,
        total_cost: line.totalCost,
        unit_cost: line.unitCost,
        order_placed_date: target.orderPlacedDate,
        order_delivery_date: target.orderDeliveryDate,
        pallets: null,
        status: ADDED_LINE_SOURCE_STATUS,
        __isNewLine: true,
      };

      handleApprovedToggle(
        buildPoRowKey(rowData, runAttemptId, isGoodchop),
        rowData,
        true,
      );
      // Deliberate creation, not a checkbox tick — don't make the planner wait
      // out the 20-second batch window to see the line appear.
      void flushPendingSaves();
    },
    [
      mainRun,
      lastSuccessfulRun,
      handleApprovedToggle,
      flushPendingSaves,
      isGoodchop,
    ],
  );

  // A PO-number edit on the Approved tab has no checkbox to fire, so committing
  // it re-approves the row with the new number. The save then propagates that
  // number to the PO's other SKU lines, which the grid edits one at a time.
  const handleEditCommitted = React.useCallback(
    (
      rowData: any,
      committed: Record<string, string>,
      _originalValues?: Record<string, string>,
      changedColumns?: string[],
    ) => {
      // Key on what was just edited, not on a value diff: committedValues
      // accumulates across edits, so a stale po_number from an earlier edit
      // would re-approve the row when the planner later changes a quantity —
      // while a diff against originalValues (the value the row was FIRST seen
      // with, never refreshed after a save) reports no change on a revert or a
      // retype back to that value, both of which must still reach the server.
      if (changedColumns && !changedColumns.includes("po_number")) return;
      const committedNumber = committed?.po_number;
      if (committedNumber === undefined) return;
      // An emptied cell is still sent. On a single-line PO the save reassigns a
      // fresh sequence; on a multi-line PO the blank inherits its siblings'
      // number back, because a group keeps the number its other lines already
      // carry. That is the safer of the two — a number already on a vendor's
      // PDF does not change under them — but it is not "renumber it for me".
      // Returning early here instead would leave the edit stored as unapproved
      // and arm the navigation guard for the rest of the session.
      const typed = (committedNumber ?? "").trim();
      const runAttemptId =
        mainRun?.runSettings?.runAttemptId ||
        lastSuccessfulRun?.runSettings?.runAttemptId ||
        "no-run";
      const withNumber = { ...rowData, po_number: typed };
      const rowKey = buildPoRowKey(withNumber, runAttemptId, isGoodchop);
      // Committing the edit stored it with approved:false a moment ago, so the
      // toggle's "was it already approved?" read would say no and count this as
      // a new approval. Restore the truth first: the row is approved, this is
      // an edit to it, and the count must not move.
      supplyGuard?.setRowApproved?.(rowKey, true);
      handleApprovedToggle(rowKey, withNumber, true);
      void flushPendingSaves();
    },
    [
      mainRun,
      lastSuccessfulRun,
      handleApprovedToggle,
      flushPendingSaves,
      isGoodchop,
      supplyGuard,
    ],
  );

  const CustomControlsWithApprovedCount = React.useCallback(
    (props: any) => {
      // One "Add SKU" button for the whole tab: the PO is chosen inside the
      // dialog, so it does not belong on any individual row.
      const status = (props?.params?.filters ?? []).find(
        (f: any) => f?.[0] === "status",
      )?.[2]?.[0];
      // On the Approved tab, not Needs Review: an added line is approved on
      // creation and lands here, and the duplicate check is built from the rows
      // of whichever tab the button sits on — the orders on that tab are the set
      // a collision can actually happen against.
      const showAddSku =
        isGoodchop && (status === APPROVED_STATUS || status === OPEN_STATUS);
      return (
        <SupplyWalkEditableCustomControls
          {...props}
          extraActions={
            showAddSku ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleOutlineIcon fontSize="small" />}
                onClick={handleAddSkuOpen}
                sx={{ height: "32px", fontSize: 12, whiteSpace: "nowrap" }}
              >
                Add SKU
              </Button>
            ) : undefined
          }
        />
      );
    },
    [isGoodchop, handleAddSkuOpen],
  );

  const handleNucleusExplain = React.useCallback(
    (row: Record<string, unknown>, rowKey: string) => {
      const acceptedView = acceptedPoInboxView;
      const currentAcceptedView = acceptedPoInboxViewRef.current;
      const localState = rowEditStateRef.get(rowKey);
      const persistedState = supplyGuard?.getEdit?.(rowKey);
      const hasCommittedChanges = (
        state:
          | Readonly<{
              committedValues?: Record<string, string>;
              originalValues?: Record<string, string>;
            }>
          | undefined,
      ): boolean =>
        Boolean(
          state?.committedValues &&
            Object.keys(state.committedValues).some(
              (field) =>
                state.committedValues?.[field] !==
                state.originalValues?.[field],
            ),
        );
      const dirty = Boolean(
        localState?.isEditing ||
          hasCommittedChanges(localState) ||
          hasCommittedChanges(persistedState),
      );
      const approvalPending = Boolean(
        pendingSavesRef.current.has(rowKey) ||
          supplyGuard?.pendingApprovalDeltas?.has(rowKey),
      );
      if (
        !supportsPoInboxNucleusRef.current ||
        !activeRun ||
        !activeRunIdentity ||
        activeRunIdentityRef.current !== activeRunIdentity ||
        !acceptedView ||
        !currentAcceptedView ||
        currentAcceptedView.sourceIdentity !== acceptedView.sourceIdentity ||
        currentAcceptedView.runIdentity !== acceptedView.runIdentity ||
        currentAcceptedView.status !== acceptedView.status ||
        currentAcceptedView.viewContext !== acceptedView.viewContext ||
        !isExplainablePoInboxStatus(acceptedView.status) ||
        acceptedView.runIdentity !== activeRunIdentity ||
        getRunIdentity(acceptedView.source.run) !== activeRunIdentity ||
        !registerActiveMetricContext
      ) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
      }
      if (dirty) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_DIRTY_PO_REASON);
      }
      if (approvalPending) {
        return rejectNucleusExplainActivation(
          NUCLEUS_EXPLAIN_PENDING_PO_REASON,
        );
      }
      if (isAwaitingResponse || pendingPoInboxRowRef.current) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_BUSY_REASON);
      }
      const context = createPoInboxRowContext({
        receiptId: acceptedView.source.receiptId,
        intent: "why_proposed",
        row,
        dirty,
        approvalPending,
      });
      if (!context || context.receiptId !== acceptedView.source.receiptId) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
      }

      const rowIdentity = `${acceptedView.sourceIdentity}\u0000${rowKey}`;
      if (activeRowIdentityRef.current !== rowIdentity) {
        releaseActiveMetricContextRef.current?.();
        releaseActiveMetricContextRef.current = registerActiveMetricContext(
          context,
          acceptedView.viewContext,
        );
        activeRowIdentityRef.current = rowIdentity;
      }
      const pendingRow = {
        context,
        prompt: poInboxRowPrompt(context),
        sourceIdentity: acceptedView.sourceIdentity,
        runIdentity: acceptedView.runIdentity,
        viewContext: acceptedView.viewContext,
        connectionReady: false,
      } as const;
      setPendingPoInboxRow(pendingRow);
      setIsSidebarOpen(true);
      return awaitNucleusExplainStart(
        () => connectChat(activeRun, "/po-inbox"),
        () => {
          clearPendingPoInboxRow(pendingRow);
          if (activeRowIdentityRef.current === rowIdentity) {
            releaseActiveMetricContextRef.current?.();
            releaseActiveMetricContextRef.current = null;
            activeRowIdentityRef.current = undefined;
          }
        },
        () => {
          const currentView = acceptedPoInboxViewRef.current;
          if (
            !supportsPoInboxNucleusRef.current ||
            activeRunIdentityRef.current !== pendingRow.runIdentity ||
            activeRowIdentityRef.current !== rowIdentity ||
            !currentView ||
            currentView.sourceIdentity !== pendingRow.sourceIdentity ||
            currentView.runIdentity !== pendingRow.runIdentity ||
            currentView.viewContext !== pendingRow.viewContext
          ) {
            return false;
          }
          return replacePendingPoInboxRow(
            pendingRow,
            Object.freeze({ ...pendingRow, connectionReady: true }),
          );
        },
      );
    },
    [
      acceptedPoInboxView,
      activeRun,
      activeRunIdentity,
      clearPendingPoInboxRow,
      connectChat,
      isAwaitingResponse,
      pendingPoInboxRowRef,
      registerActiveMetricContext,
      replacePendingPoInboxRow,
      rowEditStateRef,
      setPendingPoInboxRow,
      setIsSidebarOpen,
      supplyGuard,
    ],
  );

  React.useEffect(() => {
    if (!pendingPoInboxRow) return;
    if (
      !supportsPoInboxNucleus ||
      !acceptedPoInboxView ||
      acceptedPoInboxView.sourceIdentity !== pendingPoInboxRow.sourceIdentity ||
      acceptedPoInboxView.runIdentity !== pendingPoInboxRow.runIdentity ||
      activeRunIdentity !== pendingPoInboxRow.runIdentity ||
      acceptedPoInboxView.viewContext !== pendingPoInboxRow.viewContext
    ) {
      clearPendingPoInboxRow(pendingPoInboxRow);
      return;
    }
    if (
      !pendingPoInboxRow.connectionReady ||
      !isConnected ||
      isConnecting ||
      isAwaitingResponse
    ) {
      return;
    }

    sendMessage(
      pendingPoInboxRow.prompt,
      pendingPoInboxRow.context,
      pendingPoInboxRow.viewContext,
    );
    clearPendingPoInboxRow(pendingPoInboxRow);
  }, [
    acceptedPoInboxView,
    activeRunIdentity,
    clearPendingPoInboxRow,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    pendingPoInboxRow,
    sendMessage,
    supportsPoInboxNucleus,
  ]);
  // GoodChop's CSV exports must match the old-stack column shape so planner
  // tooling downstream doesn't break. The default Download path serializes
  // the UI's arrow table (with __expand / __actions / etc.); for goodchop we
  // route to dedicated `goodchop_*_export` view-transforms that produce the
  // exact 12- and 18-column formats the old stack emitted. Other customers
  // continue to use the default in-memory export path.
  const customHandleDownload = React.useCallback(
    (params: any) => {
      if (!isGoodchop) {
        throw new Error("not goodchop — fall through to default export");
      }

      const statusFilter = params?.filters?.find?.(
        (f: any) => Array.isArray(f) && f[0] === "status",
      );
      const currentStatus =
        statusFilter && Array.isArray(statusFilter[2])
          ? statusFilter[2][0]
          : null;

      const exportPlan = goodchopExportPlanForStatus(currentStatus);
      if (!exportPlan) {
        throw new Error("active tab has no goodchop export — fall through");
      }

      const runSettings =
        mainRun?.runSettings ?? lastSuccessfulRun?.runSettings;
      if (!runSettings) {
        throw new Error("no run selected — fall through to default export");
      }

      const runCreatedAt = (mainRun ?? lastSuccessfulRun)?.createdAt ?? null;

      // Run exports sequentially so the perspective server's transform cache
      // hits populate cleanly and the user gets predictable download ordering.
      // CSV only. The vendor-facing PO PDFs are a separate button/gesture
      // (customHandleDownloadPdf) so the two artifacts pull independently.
      (async () => {
        for (const e of exportPlan.exports) {
          await runGoodchopExportDownload({
            apolloClient: perspectiveApiService.getClient(),
            transformId: e.transformId,
            runSettings,
            filenamePrefix: e.filenamePrefix,
            runCreatedAt,
          });
        }
      })().catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message: `Export failed: ${message}`,
        });
      });
    },
    [
      isGoodchop,
      mainRun,
      lastSuccessfulRun,
      perspectiveApiService,
      addSystemMessage,
    ],
  );

  // Approved-tab companion to the CSV Download: renders one vendor-facing PO PDF
  // per approved order (backend-gateway) and downloads them as a zip. Returns the
  // promise so the toolbar's PDF button can spin until the zip resolves.
  const customHandleDownloadPdf = React.useCallback(
    async (params: any) => {
      if (!isGoodchop) return;

      const statusFilter = params?.filters?.find?.(
        (f: any) => Array.isArray(f) && f[0] === "status",
      );
      const currentStatus =
        statusFilter && Array.isArray(statusFilter[2])
          ? statusFilter[2][0]
          : null;
      if (currentStatus !== APPROVED_STATUS) return;

      const runSettings =
        mainRun?.runSettings ?? lastSuccessfulRun?.runSettings;
      if (!runSettings) return;

      try {
        await runGoodchopPdfZipDownload({
          apolloClient: apiService.getClient(),
          runSettings,
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message: `PDF export failed: ${message}`,
        });
      }
    },
    [isGoodchop, mainRun, lastSuccessfulRun, apiService, addSystemMessage],
  );

  return (
    <>
      <TransformedCustomViewContainer
        viewControllerConfig={
          {
            datasetName: "po_plan_edit",
            transformId: isGoodchop
              ? "supply_walk_editable_goodchop"
              : "supply_walk_editable",
            folder: "outputs",
            title: "Purchase Order Inbox (Beta)",
            viewContextSurfaceId: "po-inbox",
            customHandleDownload: isGoodchop ? customHandleDownload : undefined,
            customHandleDownloadPdf: isGoodchop
              ? customHandleDownloadPdf
              : undefined,
            dateHierarchyColumnFilter: null,
            excludedSegmentationsFilter: [],
            measureColumnFilter: null,
            excludedFilterColumnFilter: [],
            columnHeaderMap: isGoodchop
              ? {
                  purchase_order_id: "PO Number",
                  po_number: "PO Number",
                  po_type: "Type",
                  sku: "SKU",
                  sku_name: "Description",
                  location: "Location",
                  supplier: "Supplier",
                  quantity: "Quantity",
                  order_placed_date: "PO Date",
                  order_delivery_date: "ETA",
                  unit_cost: "Unit Cost",
                  total_cost: "Total Cost",
                  status: "Status",
                  pallets: "Pallets",
                  modification_reason: "Reason",
                  targeted_po_arrival: "Target Arrival",
                  targeted_po_placement: "Target Placement",
                  baseline_pallets: "Original Pallets",
                  baseline_quantity: "Original Quantity",
                }
              : {
                  purchase_order_id: "PO ID",
                  sku: "SKU",
                  sku_name: "SKU name",
                  reporting_attribution: "Reporting Attribute",
                  delivery_location: "Delivering to",
                  order_placed_date: "PO day",
                  order_delivery_date: "ETA day",
                  total_cost: "total cost",
                  unit_cost: "unit cost",
                },
            showControls: [
              PivotControl.Download,
              ...(isGoodchop ? [PivotControl.DownloadPdf] : []),
              PivotControl.ShowSettings,
              PivotControl.StoreAsDefault,
            ],
            customControlPanel: CustomControlsWithApprovedCount,
            paramsToTransformArgs: (
              params: DatePivotParams,
            ): Record<string, any> => {
              return {
                date_field: params.dateHierarchy,
                group_by_fields: [],
                filters: params.filters,
              };
            },
            onRenderedSourceChange: handleRenderedSourceChange,
            tableType: "flat",
            getMatrix: (params: any) => {
              // Try multiple ways to get the current status
              let currentStatus = "";

              // Method 1: Check params.filters
              if (params.filters) {
                const statusFilter = params.filters.find(
                  (filter: any) => filter[0] === "status",
                );
                currentStatus = statusFilter ? statusFilter[2][0] : "";
              }

              // Method 2: Check if status is directly in params
              if (!currentStatus && params.status) {
                currentStatus = params.status;
              }

              // Method 3: Check viewParams.filters
              if (!currentStatus && params.viewParams?.filters) {
                const statusFilter = params.viewParams.filters.find(
                  (filter: any) => filter[0] === "status",
                );
                currentStatus = statusFilter ? statusFilter[2][0] : "";
              }

              return getSupplyWalkEditableViewMatrix({
                ...params,
                viewParams: { ...(params?.viewParams || {}) },
                mainRun,
                lastSuccessfulRun,
                NestedDetailsComponent: SupplyWalkEditableNestedDetails,
                currentStatus,
                rowEditStateRef,
                onApprovedToggle: handleApprovedToggle,
                supplyGuard,
                isGoodchop,
                // The buckets the Add SKU button sits on. Each publishes its own
                // tab's orders as the targets a line can join, and its own rows
                // as the set an added line could collide with.
                poListRef:
                  isGoodchop &&
                  (currentStatus === APPROVED_STATUS ||
                    currentStatus === OPEN_STATUS)
                    ? poListRef
                    : undefined,
                onEditCommitted: isGoodchop ? handleEditCommitted : undefined,
                onNucleusExplain:
                  supportsPoInboxNucleus &&
                  isExplainablePoInboxStatus(acceptedPoInboxView?.status) &&
                  acceptedPoInboxView?.status === currentStatus &&
                  registerActiveMetricContext
                    ? handleNucleusExplain
                    : undefined,
                nucleusExplainDisabled:
                  isAwaitingResponse ||
                  isConnecting ||
                  Boolean(pendingPoInboxRow),
              });
            },
          } as any
        }
        defaultConfig={isGoodchop ? defaultConfigGoodchop : defaultConfig}
      />
      <AddSkuDialog
        open={addSkuOpen}
        onClose={() => setAddSkuOpen(false)}
        onConfirm={handleAddSkuConfirm}
        catalog={skuCatalog}
        approvedPos={approvedPos}
      />
      <Dialog
        open={pendingUnapprove !== null}
        onClose={() => resolveUnapprove(false)}
        maxWidth="xs"
      >
        <DialogTitle>
          Un-approve PO{" "}
          {String(pendingUnapprove?.rowData?.purchase_order_id ?? "")}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A purchase order goes to the vendor whole, so this takes every line
            of it off the send list — not just this one.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Quantities you changed on this PO go back to the engine&apos;s
            numbers, and any SKU you added to it by hand is removed. Neither can
            be restored: you would have to make the changes again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resolveUnapprove(false)}>Keep it</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => resolveUnapprove(true)}
          >
            Un-approve
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
